import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, RefreshCw, Calendar, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { format, addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIProductionForecast = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('15');

  useEffect(() => {
    fetchAIForecast();
  }, [selectedPeriod]);

  const fetchAIForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stats/ai-production-forecast?days=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForecast(response.data);
    } catch (err) {
      console.error('Failed to fetch AI forecast:', err);
      setError(err.response?.data?.detail || 'Failed to load AI forecast');
    } finally {
      setLoading(false);
    }
  };

  const regenerateForecast = async () => {
    setGenerating(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/stats/ai-production-forecast/generate?days=${selectedPeriod}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForecast(response.data);
    } catch (err) {
      console.error('Failed to generate AI forecast:', err);
      setError(err.response?.data?.detail || 'Failed to generate AI forecast');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="ai-production-forecast">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            <p className="text-gray-500">Loading AI forecast...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ai-production-forecast">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            AI-Powered Production Forecast
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <TabsList>
                <TabsTrigger value="5">5 Days</TabsTrigger>
                <TabsTrigger value="15">15 Days</TabsTrigger>
                <TabsTrigger value="30">30 Days</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              variant="outline"
              size="sm"
              onClick={regenerateForecast}
              disabled={generating}
              data-testid="regenerate-ai-forecast"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              {generating ? 'Generating...' : 'Regenerate'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <AlertCircle className="w-12 h-12 text-red-400" />
            <p className="text-red-600">{error}</p>
            <Button onClick={regenerateForecast} disabled={generating}>
              Try Again
            </Button>
          </div>
        ) : forecast ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-600 font-medium">AI Predicted Total</p>
                <p className="text-2xl font-bold text-purple-800">
                  {(forecast.total_predicted_kg / 1000).toFixed(1)} tonnes
                </p>
                <p className="text-xs text-purple-500">{forecast.total_predicted_kg?.toLocaleString()} kg</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-600 font-medium">Standard Estimate</p>
                <p className="text-2xl font-bold text-blue-800">
                  {(forecast.standard_estimate_kg / 1000).toFixed(1)} tonnes
                </p>
                <p className="text-xs text-blue-500">{forecast.standard_estimate_kg?.toLocaleString()} kg</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-600 font-medium">Confidence Level</p>
                <p className="text-2xl font-bold text-emerald-800">{forecast.confidence || 'N/A'}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-600 font-medium">Data Points Used</p>
                <p className="text-2xl font-bold text-amber-800">{forecast.data_points_analyzed || 0}</p>
                <p className="text-xs text-amber-500">Historical production logs</p>
              </div>
            </div>

            {/* Flush Breakdown Section */}
            {forecast.flush_breakdown && (
              <div className="grid grid-cols-2 gap-4" data-testid="ai-flush-breakdown">
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-indigo-800">Flush 1 Production</span>
                    <Badge className="bg-indigo-100 text-indigo-700">
                      {forecast.flush_breakdown.flush1_rooms} room{forecast.flush_breakdown.flush1_rooms !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <p className="text-2xl font-bold text-indigo-900">
                    {(forecast.flush_breakdown.flush1_total_kg / 1000).toFixed(1)} tonnes
                  </p>
                  <p className="text-xs text-indigo-600">
                    {forecast.flush_breakdown.flush1_total_kg?.toLocaleString()} kg
                    ({forecast.total_predicted_kg > 0 ? ((forecast.flush_breakdown.flush1_total_kg / forecast.total_predicted_kg) * 100).toFixed(0) : 0}%)
                  </p>
                  <p className="text-xs text-indigo-500 mt-1">
                    ~5.5t/room × {forecast.flush_breakdown.flush1_rooms} = {(5.5 * forecast.flush_breakdown.flush1_rooms).toFixed(1)}t est.
                  </p>
                </div>
                <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-violet-800">Flush 2 Production</span>
                    <Badge className="bg-violet-100 text-violet-700">
                      {forecast.flush_breakdown.flush2_rooms} room{forecast.flush_breakdown.flush2_rooms !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <p className="text-2xl font-bold text-violet-900">
                    {(forecast.flush_breakdown.flush2_total_kg / 1000).toFixed(1)} tonnes
                  </p>
                  <p className="text-xs text-violet-600">
                    {forecast.flush_breakdown.flush2_total_kg?.toLocaleString()} kg
                    ({forecast.total_predicted_kg > 0 ? ((forecast.flush_breakdown.flush2_total_kg / forecast.total_predicted_kg) * 100).toFixed(0) : 0}%)
                  </p>
                  <p className="text-xs text-violet-500 mt-1">
                    ~3.5t/room × {forecast.flush_breakdown.flush2_rooms} = {(3.5 * forecast.flush_breakdown.flush2_rooms).toFixed(1)}t est.
                  </p>
                </div>
              </div>
            )}

            {/* AI Analysis */}
            {forecast.ai_analysis && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-600" />
                  AI Analysis & Insights
                </h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{forecast.ai_analysis}</p>
              </div>
            )}

            {/* Day-wise Forecast */}
            {forecast.daily_forecast && forecast.daily_forecast.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  Day-wise AI Prediction
                </h4>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {forecast.daily_forecast.map((day, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      data-testid={`ai-forecast-day-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center w-16">
                          <p className="text-xs text-gray-500">{format(new Date(day.date), 'EEE')}</p>
                          <p className="font-medium">{format(new Date(day.date), 'MMM d')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {day.rooms_harvesting} room{day.rooms_harvesting !== 1 ? 's' : ''} harvesting
                          </p>
                          {day.notes && (
                            <p className="text-xs text-gray-500">{day.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-purple-700">{day.predicted_kg?.toLocaleString()} kg</p>
                        {day.adjustment_factor && day.adjustment_factor !== 1 && (
                          <Badge className={
                            day.adjustment_factor > 1 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }>
                            {day.adjustment_factor > 1 ? '+' : ''}{((day.adjustment_factor - 1) * 100).toFixed(0)}% vs standard
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Factors Considered */}
            {forecast.factors_considered && forecast.factors_considered.length > 0 && (
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-800 mb-2">Factors Considered by AI</h4>
                <div className="flex flex-wrap gap-2">
                  {forecast.factors_considered.map((factor, idx) => (
                    <Badge key={idx} variant="outline" className="bg-gray-50">
                      {factor}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Last Updated */}
            {forecast.generated_at && (
              <p className="text-xs text-gray-400 text-right">
                Generated: {format(new Date(forecast.generated_at), 'PPpp')}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Brain className="w-16 h-16 text-gray-300" />
            <p className="text-gray-500">No AI forecast available yet</p>
            <Button onClick={regenerateForecast} disabled={generating}>
              Generate AI Forecast
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIProductionForecast;
